import moment from 'moment-timezone';

/**
 * Fuseau de référence des règles de passage (date de début, date butoir).
 * Engage raisonne au jour de Paris : le front fait de même, quel que soit
 * le fuseau du navigateur.
 */
export const PARIS_TIMEZONE = 'Europe/Paris';

const ISO_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Jour civil de Paris (AAAA-MM-JJ) d'une date renvoyée par l'API.
 * - une date seule (« 2027-06-15 ») est déjà un jour civil : renvoyée telle quelle ;
 * - un horodatage est ramené au jour de Paris
 *   (« 2027-06-14T22:30:00Z » donne « 2027-06-15 »).
 * Renvoie null si la valeur est absente ou illisible.
 */
export function toParisDay(value: Date | string | null | undefined): string | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    if (typeof value === 'string' && ISO_DAY_PATTERN.test(value)) {
        return value;
    }
    const parsed = moment(value);
    if (!parsed.isValid()) {
        return null;
    }
    return parsed.tz(PARIS_TIMEZONE).format('YYYY-MM-DD');
}

/** Jour de Paris (AAAA-MM-JJ) à l'instant donné, par défaut maintenant. */
export function parisToday(now: Date | moment.Moment = new Date()): string {
    return moment(now).tz(PARIS_TIMEZONE).format('YYYY-MM-DD');
}

/**
 * True si, à Paris, on est encore strictement avant le jour donné.
 * Le jour lui-même n'est plus « avant » : une date de début au 15/06
 * est atteinte dès le 15/06 à 00:00, heure de Paris.
 */
export function isBeforeParisDay(
    value: Date | string | null | undefined,
    now: Date | moment.Moment = new Date(),
): boolean {
    const day = toParisDay(value);
    return !!day && parisToday(now) < day;
}

/**
 * Formate le jour de Paris d'une date (format moment, ex. 'DD/MM' ou 'DD/MM/YYYY').
 * Chaîne vide si la date est absente ou illisible.
 */
export function formatParisDay(
    value: Date | string | null | undefined,
    format = 'DD/MM/YYYY',
): string {
    const day = toParisDay(value);
    return day ? moment(day, 'YYYY-MM-DD').format(format) : '';
}

/**
 * Jour choisi dans un datepicker (date locale à minuit) envoyé comme début de ce
 * jour à Paris : le jour reste le même quel que soit le fuseau du navigateur.
 */
export function localDateToParisStartOfDay(date: Date): string {
    return moment.tz(moment(date).format('YYYY-MM-DD'), 'YYYY-MM-DD', PARIS_TIMEZONE).toISOString();
}

/**
 * Jour de Paris converti en date locale à minuit, pour les bornes d'un datepicker
 * (min / max) : le datepicker raisonne en jours du navigateur.
 */
export function parisDayToLocalDate(value: Date | string | null | undefined): Date | null {
    const day = toParisDay(value);
    return day ? moment(day, 'YYYY-MM-DD').toDate() : null;
}
